import { Button, Image, Typography } from "antd";
import { useRef, useState } from "react";
import ScenarioSelection from "./ScenarioSelection";
import DisplayConfiguration from "./DisplayConfiguration";
import {
  DownOutlined,
  LeftOutlined,
  MinusCircleFilled,
  MinusCircleOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import moment from "moment";
import DesiredMockup from "./DesiredMockup";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import LOGO from "../assets/logo/logo.webp";
import ImageViewer from "./ImageViewer";
import HUMAN from "../assets/human_image.png";
import PowerWiring from "./PowerWiring";
import NetworkWiring from "./NetworkWiring";

const DashboardCalculator = () => {
  const [wallWidth, setWallWidth] = useState(10);
  const [wallHeight, setWallHeight] = useState(8);
  const [column, setColumn] = useState(1);
  const [row, setRow] = useState(1);
  const [unit, setUnit] = useState("feet");

  const [selectedSeries, setSelectedSeries] = useState("");
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedModelData, setSelectedModelData] = useState(null);
  const [selectedDrawing, setSelectedDrawing] = useState("network");

  const [createDrawing, setCreateDrawing] = useState(false);
  const [useImage, setUseImage] = useState(false);
  const containerRef = useRef(null);

  // Image viewer states
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [currentImageData, setCurrentImageData] = useState("");
  const [currentImageTitle, setCurrentImageTitle] = useState("");
  const [wiringImageData, setWiringImageData] = useState("");
  const [networkImageData, setNetworkImageData] = useState("");
  const [currentDrawingType, setCurrentDrawingType] = useState("network");

  const innerWidthSize = window.innerWidth;
  const innerHeightSize = window.innerHeight;
  const finalBox =
    innerHeightSize > innerWidthSize
      ? innerWidthSize - 50
      : innerHeightSize - 50;

  const drawer = 400;
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const captureCanvas = async (ref) => {
    return await html2canvas(ref.current, {
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#fff",
      scale: 2, // Higher quality
    });
  };

  const convertToImage = async (ref, title, type) => {
    try {
      // console.log("Converting to image:", title, "Type:", type);

      // Temporarily make the container visible and positioned on-screen
      if (ref.current) {
        ref.current.style.position = "fixed";
        ref.current.style.left = "0px";
        ref.current.style.top = "0px";
        ref.current.style.visibility = "visible";
        ref.current.style.zIndex = "-1";
      }

      // Wait for DOM to update after making container visible
      await new Promise((resolve) => setTimeout(resolve, 200));

      const canvas = await captureCanvas(ref);
      const imageData = canvas.toDataURL("image/png", 1.0);

      // console.log("Generated image data length:", imageData.length);

      // Restore the hidden state
      if (ref.current) {
        ref.current.style.position = "absolute";
        ref.current.style.left = "-9999px";
        ref.current.style.top = "-9999px";
        ref.current.style.visibility = "hidden";
        ref.current.style.zIndex = "auto";
      }

      // Store the image data based on type
      if (type === "wiring") {
        setWiringImageData(imageData);
        // console.log("Stored wiring image data, length:", imageData.length);
      } else if (type === "network") {
        setNetworkImageData(imageData);
        // console.log("Stored network image data, length:", imageData.length);
      }

      setCurrentImageData(imageData);
      setCurrentImageTitle(title);
      setCurrentDrawingType(type);
      setImageViewerVisible(true);

      // console.log("Final current image data length:", imageData.length);
    } catch (error) {
      console.error("Error converting to image:", error);

      // Ensure the container is hidden even if there's an error
      if (ref.current) {
        ref.current.style.position = "absolute";
        ref.current.style.left = "-9999px";
        ref.current.style.top = "-9999px";
        ref.current.style.visibility = "hidden";
        ref.current.style.zIndex = "auto";
      }
    }
  };

  const switchDrawing = () => {
    const newType = currentDrawingType === "network" ? "wiring" : "network";
    const newImageData =
      newType === "network" ? networkImageData : wiringImageData;
    const newTitle =
      newType === "network" ? "Network Wiring Diagram" : "Power Wiring Diagram";

    // console.log("Switching from", currentDrawingType, "to", newType);
    // console.log("Network image data length:", networkImageData.length);
    // console.log("Wiring image data length:", wiringImageData.length);
    // console.log("New image data length:", newImageData.length);

    if (newImageData && newImageData.length > 0) {
      setCurrentImageData(newImageData);
      setCurrentImageTitle(newTitle);
      setCurrentDrawingType(newType);
    } else {
      console.log("No image data available for", newType);
      // If no image data is available, convert it now
      setTimeout(async () => {
        if (containerRef.current) {
          await convertToImage(containerRef, newTitle, newType);
        }
      }, 200);
    }
  };

  const downloadAs = async (format) => {
    const canvas = await captureCanvas(containerRef);

    if (format === "jpg" || format === "png") {
      const imageType = format === "jpg" ? "image/jpeg" : "image/png";

      // console.log(canvas.toDataURL(imageType, 1.0));
      const link = document.createElement("a");
      link.download = "drawing." + format;
      link.href = canvas.toDataURL(imageType, 1.0);
      link.click();
    } else if (format === "pdf") {
      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(imgData, "JPEG", 0, 0, canvas.width, canvas.height);
      pdf.save("drawing.pdf");
    }
  };

  const handleCreateDrawings = async () => {
    if (!createDrawing) {
      // When opening drawings, convert both containers to images
      setCreateDrawing(true);

      // Wait longer for the DOM to update, then convert to images
      setTimeout(async () => {
        if (containerRef.current) {
          // First capture network image
          await convertToImage(
            containerRef,
            "Network Wiring Diagram",
            "network"
          );

          // Switch to wiring view temporarily to capture wiring image
          setSelectedDrawing("wiring");
          setTimeout(async () => {
            if (containerRef.current) {
              await convertToImage(
                containerRef,
                "Power Wiring Diagram",
                "wiring"
              );
            }
            // Switch back to network view and ensure we show the network image
            setSelectedDrawing("network");
            // Use the stored network image data
            if (networkImageData && networkImageData.length > 0) {
              setCurrentImageData(networkImageData);
              setCurrentImageTitle("Network Wiring Diagram");
              setCurrentDrawingType("network");
              setImageViewerVisible(true);
            }
          }, 500);
        }
      }, 300);
    } else {
      setCreateDrawing(false);
      setImageViewerVisible(false);
    }
  };

  const handleDrawingTypeChange = async (type) => {
    setSelectedDrawing(type);

    // Convert the appropriate container to image with longer delay
    setTimeout(async () => {
      const ref = type === "network" ? containerRef : containerRef;
      const title =
        type === "network" ? "Network Wiring Diagram" : "Power Wiring Diagram";
      if (ref.current) {
        await convertToImage(ref, title, type);
      }
    }, 400); // Increased from 100ms to 400ms
  };

  //CALCULATION
  // const actualWidthMM = wallWidth * 1000;

  // // 1. Calculate Pixel Pitch (in mm)
  // const pixelPitch =
  //   actualWidthMM /
  //   (column * selectedModelData !== null
  //     ? selectedModelData.moduleResolution[0].width
  //     : 0);

  // console.log("selectedModelData: ", selectedModelData);

  // // 2. Calculate LED Wall Diagonal (in meters)
  // const ledDiagonal = Math.sqrt(
  //   Math.pow(wallWidth, 2) + Math.pow(wallHeight, 2)
  // );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        width: "100vw",
      }}
    >
      <div
        style={{
          width: `calc(100% - ${drawer}px)`,
          height: "100%",
          borderRight: "1px solid #aeaeae",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#000",
          // border: "1px solid yellow",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            // border: "1px solid blue",
            // height: "100%",
          }}
        >
          {/* <Typography
            style={{ marginBottom: "20px", color: "#FFF", fontSize: 20 }}
          >
            Dashboard Calculator
          </Typography> */}

          {selectedSeries && selectedModel ? (
            <>
              <div
                style={{
                  position: "relative",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  style={{
                    textAlign: "center",
                    marginBottom: 15,
                    color: "#FFF",
                    fontSize: 18,
                    fontWeight: "bold",
                  }}
                >
                  VISUAL PREVIEW
                </Typography>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    position: "relative",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      paddingTop: 30,
                      paddingBottom: 30,
                      display: "flex",
                      alignItems: "end",
                      opacity: 0.2,
                    }}
                  >
                    {unit === "meter" ? (
                      <Image
                        src={HUMAN}
                        preview={false}
                        alt="mockup"
                        style={{
                          height:
                            wallHeight >= 1.524
                              ? 1.524 * dimensions.scale
                              : wallHeight * dimensions.scale,
                          width: "100%",
                        }}
                      />
                    ) : unit === "feet" ? (
                      <Image
                        src={HUMAN}
                        preview={false}
                        alt="mockup"
                        style={{
                          height:
                            wallHeight >= 5
                              ? 5 * dimensions.scale
                              : wallHeight * dimensions.scale,
                          width: "100%",
                        }}
                      />
                    ) : null}
                  </div>
                  <DesiredMockup
                    wallWidth={wallWidth}
                    wallHeight={wallHeight}
                    unit={unit}
                    drawer={drawer}
                    selectedModelData={selectedModelData}
                    column={column}
                    row={row}
                    useImage={useImage}
                    setDimensions={setDimensions}
                    dimensions={dimensions}
                  />
                  <div
                    style={{
                      width: "100%",
                      paddingTop: 30,
                      paddingBottom: 30,
                      display: "flex",
                      alignItems: "end",
                      opacity: 0,
                    }}
                  >
                    {unit === "meter" ? (
                      <Image
                        src={HUMAN}
                        preview={false}
                        alt="mockup"
                        style={{
                          height:
                            wallHeight >= 1.524
                              ? 1.524 * dimensions.scale
                              : wallHeight * dimensions.scale,
                          width: "100%",
                        }}
                      />
                    ) : unit === "feet" ? (
                      <Image
                        src={HUMAN}
                        preview={false}
                        alt="mockup"
                        style={{
                          height:
                            wallHeight >= 5
                              ? 5 * dimensions.scale
                              : wallHeight * dimensions.scale,
                          width: "100%",
                        }}
                      />
                    ) : null}
                  </div>
                </div>

                {/* RIGHT CENTER ABSOLUTE */}
                {/* <div
                    style={{
                      transform: "rotate(90deg)",
                      position: "absolute",
                      top: "51%",
                      right: -44,
                    }}
                  >
                    <Typography
                      style={{ color: "#FFF", padding: 0, margin: 0 }}
                    >
                      {wallHeight} {unit}(s)
                    </Typography>
                  </div> */}

                {/* TOP CENTER ABSOLUTE */}
                {/* <div
                    style={{
                      position: "absolute",
                      top: 22,
                      left: "46%",
                    }}
                  >
                    <Typography style={{ color: "#FFF", textAlign: "center" }}>
                      {wallWidth} {unit}(s)
                    </Typography>
                  </div> */}
              </div>

              <div
                style={{
                  position: "absolute",
                  left: "-9999px",
                  top: "-9999px",
                  visibility: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "start",
                    gap: "10px",
                    marginBottom: 10,
                    overflow: "auto",
                    width: "100%",
                    // width: finalBox,
                  }}
                >
                  <Button
                    onClick={() => handleDrawingTypeChange("network")}
                    color={
                      selectedDrawing === "network" ? "primary" : "default"
                    }
                    variant={
                      selectedDrawing === "network" ? "solid" : "outlined"
                    }
                  >
                    Network
                  </Button>
                  <Button
                    onClick={() => handleDrawingTypeChange("wiring")}
                    color={selectedDrawing === "wiring" ? "primary" : "default"}
                    variant={
                      selectedDrawing === "wiring" ? "solid" : "outlined"
                    }
                  >
                    Wiring
                  </Button>
                </div>

                {/* DRAWING SELECTION */}
                {/* WIRING DRAWING */}
                {selectedDrawing === "wiring" && (
                  <>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        // border: "1px solid red",
                        padding: "10px",
                        backgroundColor: "#fff",
                        width: "auto",

                        // width: finalBox,
                      }}
                      ref={containerRef}
                    >
                      <PowerWiring
                        rows={row}
                        columns={column}
                        selectedModelData={selectedModelData}
                      />

                      {/* <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {Array.from({
          
                          length: row,
                        }).map((_, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              flexDirection: "row-reverse",
                            }}
                          >
                            {Array.from({
             
                              length: column,
                            }).map((_, index2) => {
                              var width =
                                unit === "meter"
                                  ? selectedModelData.panelDimensions.metric[0]
                                      .width >=
                                    selectedModelData.panelDimensions.metric[0]
                                      .height
                                    ? 40
                                    : 30
                                  : selectedModelData.panelDimensions
                                      .imperial[0].width >=
                                    selectedModelData.panelDimensions
                                      .imperial[0].height
                                  ? 40
                                  : 30;

                              var height =
                                unit === "meter"
                                  ? selectedModelData.panelDimensions.metric[0]
                                      .height >=
                                    selectedModelData.panelDimensions.metric[0]
                                      .width
                                    ? 40
                                    : 30
                                  : selectedModelData.panelDimensions
                                      .imperial[0].height >=
                                    selectedModelData.panelDimensions
                                      .imperial[0].width
                                  ? 40
                                  : 30;

                              return (
                                <div
                                  key={index2}
                                  style={{
                                    width: width,
                                    height: height,
                                    backgroundColor: "rgb(53 53 53)",
                                    border: "1px solid #aeaeae",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "relative",
                                  }}
                                >
                           
                                  {index2 === 0 && row > 1 && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        top:
                                          width > height
                                            ? 13
                                            : width === height
                                            ? 19
                                            : 19,
                                        right:
                                          width > height
                                            ? 18.6
                                            : width === height
                                            ? 21
                                            : 16,
                                        width:
                                          width > height
                                            ? 14.3
                                            : width === height
                                            ? 17
                                            : 12.2,
                                        height: 1,
                                        backgroundColor: "red",
                                        zIndex: 1,
                                      }}
                                    />
                                  )}

                           
                                  {index2 > 0 && index2 === column - 1 && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        top:
                                          width > height
                                            ? 13
                                            : width === height
                                            ? 19
                                            : 19,
                                        left:
                                          width > height
                                            ? 18.6
                                            : width === height
                                            ? 18.6
                                            : 13.6,
                                        width:
                                          width > height
                                            ? 14.3
                                            : width === height
                                            ? 19.5
                                            : 14.5,
                                        height: 1,
                                        backgroundColor: "red",
                                        zIndex: 1,
                                      }}
                                    />
                                  )}

                           
                                  {index2 !== row - 1 && index2 !== 0 && (
                                    <>
                                      <div
                                        style={{
                                          position: "absolute",
                                          top:
                                            width > height
                                              ? 13
                                              : width === height
                                              ? 19
                                              : 19,
                                          left:
                                            width > height
                                              ? 18.6
                                              : width === height
                                              ? 40
                                              : 30,
                                          width:
                                            width > height
                                              ? 28.4
                                              : width === height
                                              ? 38
                                              : 28,
                                          height: 1,
                                          backgroundColor: "red",
                                          zIndex: 1,
                                        }}
                                      />
                                    </>
                                  )}

                   
                                  {index2 === column - 1 || index2 === 0 ? (
                                    <div
                                      style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        border: "1px solid #aeaeae",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "red",
                                        zIndex: 2,
                                      }}
                                    >
                                      {index2 === 0 && row > 1 && (
                                        <LeftOutlined
                                          style={{
                                            color: "#FFF",
                                            fontSize: 5,
                                          }}
                                        />
                                      )}
                                      {index2 !== 0 &&
                                        index2 === column - 1 && (
                                          <MinusOutlined
                                            style={{
                                              color: "#FFF",
                                              fontSize: 5,
                                            }}
                                          />
                                        )}
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>

                      <div
                        style={{
                          marginTop: "10px",
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          style={{
                            color: "#000",
                            fontSize: 14,
                            textAlign: "center",
                          }}
                        >
                          Power Wiring Diagram(Front View)
                        </Typography>
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            marginTop: "5px",
                          }}
                        >
                          <div
                            style={{
                              border: "1px solid #000",
                              width: "33%",
                              display: "flex",
                              alignItems: "center",
                              paddingLeft: "8px",
                            }}
                          >
                            <Typography style={{ fontSize: 12, color: "#000" }}>
                              Panel Layout(WxH): {column}(W) {row}(H), total{" "}
                              {column * row} pc(s)
                            </Typography>
                          </div>
                          <div
                            style={{
                              border: "1px solid #000",
                              width: "34%",
                              display: "flex",
                              alignItems: "center",
                              paddingLeft: "8px",
                            }}
                          >
                            <Typography style={{ fontSize: 12, color: "#000" }}>
                              Resolution:{" "}
                              {column *
                                selectedModelData.panelResolution[0].width}
                              (W){" "}
                              {row *
                                selectedModelData.panelResolution[0].height}
                              (H), total{" "}
                              {parseInt(
                                column *
                                  selectedModelData.panelResolution[0].width
                              ) *
                                parseInt(
                                  row *
                                    selectedModelData.panelResolution[0].height
                                )}{" "}
                              Dots
                            </Typography>
                          </div>
                          <div
                            style={{
                              border: "1px solid #000",
                              width: "33%",
                              display: "flex",
                              alignItems: "center",
                              paddingLeft: "8px",
                            }}
                          >
                            <Typography style={{ fontSize: 12, color: "#000" }}>
                              Date: {moment().format("LL")}
                            </Typography>
                          </div>
                        </div>
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            marginTop: "5px",
                          }}
                        >
                          <div
                            style={{
                              border: "1px solid #000",
                              width: "33%",
                              display: "flex",
                              alignItems: "center",
                              paddingLeft: "8px",
                            }}
                          >
                            <Typography style={{ fontSize: 12, color: "#000" }}>
                              Max Power Consumption:{" "}
                              {selectedModelData.maxPowerConsumption[0].width}{" "}
                              {selectedModelData.maxPowerConsumption[0].unit}
                            </Typography>
                          </div>
                          <div
                            style={{
                              border: "1px solid #000",
                              width: "34%",
                              display: "flex",
                              alignItems: "center",
                              paddingLeft: "8px",
                            }}
                          >
                            <Typography style={{ fontSize: 12, color: "#000" }}>
                              Processor Model:{" "}
                              {selectedModelData.processorModel}
                            </Typography>
                          </div>
                          <div
                            style={{
                              border: "1px solid #000",
                              width: "33%",
                              display: "flex",
                              alignItems: "center",
                              paddingLeft: "8px",
                            }}
                          >
                            <Typography style={{ fontSize: 12, color: "#000" }}>
                              Max Pixel Capacity:{" "}
                              {selectedModelData.maxPixelCapacity[0].pixel}{" "}
                              {selectedModelData.maxPixelCapacity[0].unit}
                            </Typography>
                          </div>
                        </div>
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            marginTop: "5px",
                            border: "1px solid #000",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography style={{ fontSize: 12, color: "#000" }}>
                            Total Processor: {column * row} pc(s)
                          </Typography>
                        </div>
                      </div> */}
                    </div>
                    <div
                      style={{ display: "flex", gap: "10px", marginTop: 10 }}
                    >
                      <Button
                        onClick={() =>
                          convertToImage(
                            containerRef,
                            "Power Wiring Diagram",
                            "wiring"
                          )
                        }
                      >
                        View Image
                      </Button>
                      <Button onClick={() => downloadAs("jpg")}>
                        Download as JPG
                      </Button>
                      <Button onClick={() => downloadAs("png")}>
                        Download as PNG
                      </Button>
                      <Button onClick={() => downloadAs("pdf")}>
                        Download as PDF
                      </Button>
                    </div>
                  </>
                )}

                {/* NETWORK DRAWING */}
                {selectedDrawing === "network" && (
                  <>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        // border: "1px solid red",
                        padding: "10px",
                        backgroundColor: "#fff",
                        width: "auto",
                        // width: finalBox,
                      }}
                      ref={containerRef}
                    >
                      <NetworkWiring
                        rows={row}
                        columns={column}
                        selectedModelData={selectedModelData}
                      />
                      {/* <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {Array.from({
                          // length:
                          //   selectedModelData.modulesPerPanel[0].width * column,
                          length: row,
                        }).map((_, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              flexDirection: "row-reverse",
                            }}
                          >
                            {Array.from({
                              length: column,
                            }).map((_, index2) => {
                              var width =
                                unit === "meter"
                                  ? selectedModelData.panelDimensions.metric[0]
                                      .width >=
                                    selectedModelData.panelDimensions.metric[0]
                                      .height
                                    ? 40
                                    : 30
                                  : selectedModelData.panelDimensions
                                      .imperial[0].width >=
                                    selectedModelData.panelDimensions
                                      .imperial[0].height
                                  ? 40
                                  : 30;

                              var height =
                                unit === "meter"
                                  ? selectedModelData.panelDimensions.metric[0]
                                      .height >=
                                    selectedModelData.panelDimensions.metric[0]
                                      .width
                                    ? 40
                                    : 30
                                  : selectedModelData.panelDimensions
                                      .imperial[0].height >=
                                    selectedModelData.panelDimensions
                                      .imperial[0].width
                                  ? 40
                                  : 30;

                              return (
                                <div
                                  key={index2}
                                  style={{
                                    width: width,
                                    height: height,
                                    backgroundColor: "rgb(10 43 42)",
                                    border: "1px solid #aeaeae",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "relative",
                                  }}
                                >
                                  {index2 === 0 && row > 1 && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        top:
                                          width > height
                                            ? 13
                                            : width === height
                                            ? 19
                                            : 19,
                                        right:
                                          width > height
                                            ? 18.6
                                            : width === height
                                            ? 21
                                            : 16,
                                        width:
                                          width > height
                                            ? 14.3
                                            : width === height
                                            ? 17
                                            : 12.2,
                                        height: 1,
                                        backgroundColor: "green",
                                        zIndex: 1,
                                      }}
                                    />
                                  )}

                                  {index2 > 0 && index2 === column - 1 && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        top:
                                          width > height
                                            ? 13
                                            : width === height
                                            ? 19
                                            : 19,
                                        left:
                                          width > height
                                            ? 18.6
                                            : width === height
                                            ? 18.6
                                            : 13.6,
                                        width:
                                          width > height
                                            ? 14.3
                                            : width === height
                                            ? 19.5
                                            : 14.5,
                                        height: 1,
                                        backgroundColor: "green",
                                        zIndex: 1,
                                      }}
                                    />
                                  )}

                                  {index2 !== row - 1 && index2 !== 0 && (
                                    <>
                                      <div
                                        style={{
                                          position: "absolute",
                                          top:
                                            width > height
                                              ? 13
                                              : width === height
                                              ? 19
                                              : 19,
                                          left:
                                            width > height
                                              ? 18.6
                                              : width === height
                                              ? 40
                                              : 30,
                                          width:
                                            width > height
                                              ? 28.4
                                              : width === height
                                              ? 38
                                              : 28,
                                          height: 1,
                                          backgroundColor: "green",
                                          zIndex: 1,
                                        }}
                                      />
                                    </>
                                  )}

                                  {index2 === column - 1 || index2 === 0 ? (
                                    <div
                                      style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        border: "1px solid #aeaeae",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "green",
                                        zIndex: 2,
                                      }}
                                    >
                                      {index2 === 0 && row > 1 && (
                                        <LeftOutlined
                                          style={{
                                            color: "#FFF",
                                            fontSize: 5,
                                          }}
                                        />
                                      )}
                                      {index2 !== 0 &&
                                        index2 === column - 1 && (
                                          <MinusOutlined
                                            style={{
                                              color: "#FFF",
                                              fontSize: 5,
                                            }}
                                          />
                                        )}
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                      <div
                        style={{
                          marginTop: "10px",
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          style={{
                            color: "#000",
                            fontSize: 14,
                            textAlign: "center",
                          }}
                        >
                          Network Wiring Diagram(Front View)
                        </Typography>
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            marginTop: "5px",
                          }}
                        >
                          <div
                            style={{
                              border: "1px solid #000",
                              width: "33%",
                              display: "flex",
                              alignItems: "center",
                              paddingLeft: "8px",
                            }}
                          >
                            <Typography style={{ fontSize: 12, color: "#000" }}>
                              Panel Layout(WxH): {column}(W) {row}(H), total{" "}
                              {column * row} pc(s)
                            </Typography>
                          </div>
                          <div
                            style={{
                              border: "1px solid #000",
                              width: "34%",
                              display: "flex",
                              alignItems: "center",
                              paddingLeft: "8px",
                            }}
                          >
                            <Typography style={{ fontSize: 12, color: "#000" }}>
                              Resolution:{" "}
                              {column *
                                selectedModelData.panelResolution[0].width}
                              (W){" "}
                              {row *
                                selectedModelData.panelResolution[0].height}
                              (H), total{" "}
                              {parseInt(
                                column *
                                  selectedModelData.panelResolution[0].width
                              ) *
                                parseInt(
                                  row *
                                    selectedModelData.panelResolution[0].height
                                )}{" "}
                              Dots
                            </Typography>
                          </div>
                          <div
                            style={{
                              border: "1px solid #000",
                              width: "33%",
                              display: "flex",
                              alignItems: "center",
                              paddingLeft: "8px",
                            }}
                          >
                            <Typography style={{ fontSize: 12, color: "#000" }}>
                              Date: {moment().format("LL")}
                            </Typography>
                          </div>
                        </div>
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            marginTop: "5px",
                          }}
                        >
                          <div
                            style={{
                              border: "1px solid #000",
                              width: "33%",
                              display: "flex",
                              alignItems: "center",
                              paddingLeft: "8px",
                            }}
                          >
                            <Typography style={{ fontSize: 12, color: "#000" }}>
                              Max Power Consumption:{" "}
                              {selectedModelData.maxPowerConsumption[0].width}{" "}
                              {selectedModelData.maxPowerConsumption[0].unit}
                            </Typography>
                          </div>
                          <div
                            style={{
                              border: "1px solid #000",
                              width: "34%",
                              display: "flex",
                              alignItems: "center",
                              paddingLeft: "8px",
                            }}
                          >
                            <Typography style={{ fontSize: 12, color: "#000" }}>
                              Processor Model:{" "}
                              {selectedModelData.processorModel}
                            </Typography>
                          </div>
                          <div
                            style={{
                              border: "1px solid #000",
                              width: "33%",
                              display: "flex",
                              alignItems: "center",
                              paddingLeft: "8px",
                            }}
                          >
                            <Typography style={{ fontSize: 12, color: "#000" }}>
                              Max Pixel Capacity:{" "}
                              {selectedModelData.maxPixelCapacity[0].pixel}{" "}
                              {selectedModelData.maxPixelCapacity[0].unit}
                            </Typography>
                          </div>
                        </div>
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            marginTop: "5px",
                            border: "1px solid #000",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography style={{ fontSize: 12, color: "#000" }}>
                            Total Processor: {column * row} pc(s)
                          </Typography>
                        </div>
                      </div> */}
                    </div>
                    <div
                      style={{ display: "flex", gap: "10px", marginTop: 10 }}
                    >
                      <Button
                        onClick={() =>
                          convertToImage(
                            containerRef,
                            "Network Wiring Diagram",
                            "network"
                          )
                        }
                      >
                        View Image
                      </Button>
                      <Button onClick={() => downloadAs("jpg")}>
                        Download as JPG
                      </Button>
                      <Button onClick={() => downloadAs("png")}>
                        Download as PNG
                      </Button>
                      <Button onClick={() => downloadAs("pdf")}>
                        Download as PDF
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <Typography style={{ color: "#FFF", fontSize: 14 }}>
              Please select the scenario you want to calculate & configure the
              display.
            </Typography>
          )}
        </div>
      </div>
      <div
        style={{
          width: drawer,
          height: "100%",
          padding: "40px",
          // background: "linear-gradient(to bottom, #04347a, #a611e7)",
          background: "linear-gradient(30deg, #5252bb, #1f1f66)",
          overflow: "auto",
          position: "relative",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderBottom: "1px solid #aeaeae",
          }}
        >
          <Typography
            style={{
              color: "#FFF",
              fontSize: 18,
              fontWeight: "bold",
              marginBottom: 10,
            }}
          >
            VENUE WALL SIZE
          </Typography>
        </div>
        <ScenarioSelection
          wallWidth={wallWidth}
          setWallWidth={setWallWidth}
          wallHeight={wallHeight}
          setWallHeight={setWallHeight}
          unit={unit}
          setUnit={setUnit}
          setColumn={setColumn}
          setRow={setRow}
          selectedModelData={selectedModelData}
          column={column}
          row={row}
        />

        <div
          style={{
            marginTop: 40,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderBottom: "1px solid #aeaeae",
          }}
        >
          <Typography
            style={{
              color: "#FFF",
              fontSize: 18,
              fontWeight: "bold",
              marginBottom: 10,
            }}
          >
            DISPLAY CONFIGURATION
          </Typography>
        </div>
        <DisplayConfiguration
          unit={unit}
          column={column}
          setColumn={setColumn}
          row={row}
          setRow={setRow}
          selectedSeries={selectedSeries}
          setSelectedSeries={setSelectedSeries}
          availableModels={availableModels}
          setAvailableModels={setAvailableModels}
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
          setSelectedModelData={setSelectedModelData}
          selectedModelData={selectedModelData}
          wallWidth={wallWidth}
          wallHeight={wallHeight}
          setWallWidth={setWallWidth}
          setWallHeight={setWallHeight}
        />

        {selectedSeries && selectedModel && (
          <>
            <div
              style={{
                marginTop: 40,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderBottom: "1px solid #aeaeae",
              }}
            >
              <Typography
                style={{
                  color: "#FFF",
                  fontSize: 18,
                  fontWeight: "bold",
                  marginBottom: 10,
                }}
              >
                ACTUAL SIZE
              </Typography>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: 15,
                marginBottom: 10,
              }}
            >
              <span style={{ color: "#FFF", fontSize: 12 }}>
                Series: {selectedSeries}
              </span>
              <span style={{ color: "#FFF", fontSize: 12 }}>
                Model: {selectedModelData.model}
              </span>
              <span style={{ color: "#FFF", fontSize: 12 }}>
                Size: {selectedModelData.panelDimensions.imperial[0].width}"x
                {selectedModelData.panelDimensions.imperial[0].height}"(
                {selectedModelData.panelDimensions.metric[0].width}x
                {selectedModelData.panelDimensions.metric[0].height}MM)
              </span>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: 15,
                marginBottom: 10,
              }}
            >
              <span style={{ color: "#FFF", fontSize: 12 }}>
                Actual Width:{" "}
                {unit === "meter"
                  ? `${(
                      (column *
                        selectedModelData.panelDimensions.metric[0].width) /
                      1000
                    ).toFixed(2)} m`
                  : `${(
                      (column *
                        selectedModelData.panelDimensions.imperial[0].width) /
                      12
                    ).toFixed(2)} ft`}
              </span>
              <span style={{ color: "#FFF", fontSize: 12 }}>
                Actual Height:{" "}
                {unit === "meter"
                  ? `${(
                      (row *
                        selectedModelData.panelDimensions.metric[0].height) /
                      1000
                    ).toFixed(2)} m`
                  : `${(
                      (row *
                        selectedModelData.panelDimensions.imperial[0].height) /
                      12
                    ).toFixed(2)} ft`}
              </span>
              <span style={{ color: "#FFF", fontSize: 12 }}>
                Display Resolution:{" "}
                {parseInt(column * selectedModelData.panelResolution[0].width)}x
                {parseInt(row * selectedModelData.panelResolution[0].height)}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: 15,
              }}
            >
              <span style={{ color: "#FFF", fontSize: 12 }}>
                Pixel Pitch:{" "}
                {unit === "meter"
                  ? (
                      selectedModelData.panelDimensions.metric[0].width /
                      selectedModelData.panelResolution[0].width
                    ).toFixed(2)
                  : (
                      (selectedModelData.panelDimensions.imperial[0].width *
                        25.4) /
                      selectedModelData.panelResolution[0].width
                    ).toFixed(2)}{" "}
                mm
              </span>
              <span style={{ color: "#FFF", fontSize: 12 }}>
                LED Wall Diagonal:{" "}
                {unit === "meter"
                  ? Math.sqrt(
                      Math.pow(
                        (selectedModelData.panelDimensions.metric[0].width *
                          column) /
                          1000,
                        2
                      ) +
                        Math.pow(
                          (selectedModelData.panelDimensions.metric[0].height *
                            row) /
                            1000,
                          2
                        )
                    ).toFixed(2)
                  : Math.sqrt(
                      Math.pow(
                        (selectedModelData.panelDimensions.imperial[0].width *
                          column) /
                          12,
                        2
                      ) +
                        Math.pow(
                          (selectedModelData.panelDimensions.imperial[0]
                            .height *
                            row) /
                            12,
                          2
                        )
                    ).toFixed(2)}{" "}
                {unit === "meter" ? "m" : "ft"}
              </span>
              <span style={{ color: "#FFF", fontSize: 12 }}>
                Total Panels: {column * row}
              </span>
              {/* <span style={{ color: "#FFF", fontSize: 12 }}>
                Total Modules:{" "}
                {column *
                  row *
                  (parseInt(selectedModelData.modulesPerPanel[0].width) *
                    parseInt(selectedModelData.modulesPerPanel[0].height))}
              
              </span> */}
            </div>

            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "5px",
                // borderBottom: "1px solid #aeaeae",
                // borderTop: "1px solid #aeaeae",
                gap: "10px",
              }}
            >
              <Button
                onClick={handleCreateDrawings}
                style={{
                  background: "linear-gradient(210deg, #5252bb, #1f1f66)",
                  color: "#FFF",
                  fontSize: 14,
                  padding: "10px 20px",
                  borderRadius: 5,
                }}
              >
                {createDrawing ? "Close Drawings" : "Create Drawings"}
              </Button>
              {/* <Button onClick={() => setUseImage(!useImage)}>
                {useImage ? "Use Default" : "Use Image"}
              </Button> */}
            </div>
          </>
        )}
        <div
          style={{
            position: "absolute",
            right: 20,
            bottom: 20,
          }}
        >
          <Image
            src={LOGO}
            preview={false}
            alt="mockup"
            style={{
              width: 80,
              height: 80,
            }}
          />
        </div>
      </div>

      <ImageViewer
        visible={imageViewerVisible}
        imageData={currentImageData}
        title={currentImageTitle}
        onClose={() => {
          setImageViewerVisible(false);
          setCreateDrawing(false);
        }}
        onSwitchDrawing={switchDrawing}
        currentDrawingType={currentDrawingType}
      />
    </div>
  );
};

export default DashboardCalculator;
